// The deal's page: what identifies the deal, its documents and its history. Documents are dropped
// here, because that is what most people open a deal to do.
//
// With the deal flow off, the fields are read here and edited on the form (DealForm.jsx).
//
// With the deal flow on (TX-US-0043), the page also carries the flow: the path under the header,
// beneath it what a passed stage holds when one is chosen, then the decision panel for whoever
// may take the open approval step, and the stage panel for a deal writer - both, first the
// decision, for a writer who also takes the step. The deal's terms join its fields, and its stage
// replaces its state. A deal writer edits the fields here, at every stage that takes an edit
// (DealStageForm.jsx); a reader, and a deal at Approval or at its end, has them read-only.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { deleteDeal, getDeal, getDealFlow } from '../api.js';
import DealDocuments from '../documents/DealDocuments.jsx';
import { useMe } from '../MeContext.js';
import { ADMIN_ROLE, DEAL_EDITOR_ROLES, holdsAny } from '../roles.js';
import Icon from '../ui/Icon.jsx';
import { notify } from '../ui/notify.jsx';
import DecisionPanel from './DecisionPanel.jsx';
import { stateLabel } from './dealForm.js';
import DealFieldList, { Field } from './DealFieldList.jsx';
import DealStageForm from './DealStageForm.jsx';
import { termLabels } from './dealTerms.js';
import DealTimeline from './DealTimeline.jsx';
import {
  STAGE_LABELS,
  editable,
  endedFrom,
  flowOn,
  isFinal,
  passedStageFrom,
} from './flow.js';
import FlowNotes from './FlowNotes.jsx';
import StageHolds from './StageHolds.jsx';
import StagePanel from './StagePanel.jsx';
import StagePath from './StagePath.jsx';
import { useDealVocabulary } from './useDealVocabulary.js';
import { useReferenceLists } from './useReferenceLists.js';
import { namedPlaceLabel } from './vocabulary.js';

const NOT_FOUND = 'That deal does not exist, or is not yours to see.';

export default function DealDetail() {
  const { preDealId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const me = useMe();
  const flow = flowOn(me);
  const vocabulary = useDealVocabulary(flow);
  // The words for the terms' codes, read only where the page shows the terms.
  const lists = useReferenceLists({ enabled: flow });
  const labels = termLabels(lists);
  // The API refuses either action without the role regardless (ADR-0012); this only avoids
  // offering a control that would be refused.
  const canEdit = holdsAny(me, DEAL_EDITOR_ROLES);
  const canDelete = holdsAny(me, [ADMIN_ROLE]);

  const [state, setState] = useState({ phase: 'loading' });
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  // A read after a move, a decision or a document that failed; the page offers to read again.
  const [readError, setReadError] = useState(null);
  // Whether the fields edited on this page hold changes not yet saved (DealStageForm.jsx).
  const [unsaved, setUnsaved] = useState(false);

  // The deal, its history and, with the flow on, what its next move waits on and whether the
  // open approval step is the reader's. Read together, so the panels never act on a deal older
  // than the one shown. Only the newest read is applied: a slower, older answer - or one for the
  // deal the page showed before - never overwrites it.
  const reads = useRef(0);
  const read = useCallback(
    (onFailure) => {
      const mine = reads.current + 1;
      reads.current = mine;
      return Promise.all([getDeal(preDealId), flow ? getDealFlow(preDealId) : null])
        .then(([data, view]) => {
          if (reads.current !== mine) return;
          setState({ phase: 'ready', ...data, view });
          setReadError(null);
        })
        .catch((err) => {
          if (reads.current === mine) onFailure(err);
        });
    },
    [preDealId, flow],
  );

  useEffect(() => {
    read((err) => setState({
      phase: 'error',
      message: err.status === 404 ? NOT_FOUND : err.message,
    }));
  }, [read]);

  // After a move, a decision or a document: the page read again, a failure said above it with
  // a way to read it again - until then, a panel whose move succeeded stays disabled.
  const reload = useCallback(() => read((err) => setReadError(err.message)), [read]);

  // What a document changes is what a move may wait on: the flow view is read again.
  const onDocumentsChanged = useCallback(() => {
    if (flow) reload();
  }, [flow, reload]);

  if (state.phase === 'loading') return <p className="muted">Loading…</p>;
  if (state.phase === 'error') {
    return (
      <>
        <p className="error">{state.message}</p>
        <Link to="/deals">Back to the register</Link>
      </>
    );
  }

  const { deal, events, view } = state;
  const fields = deal.fields ?? {};
  const number = fields.deal_number ?? deal.pre_deal_id;
  const ended = flow ? endedFrom(deal, events) : null;
  const holds = flow ? passedStageFrom(searchParams, deal, ended) : null;
  const namedPlace = namedPlaceLabel(vocabulary, fields.incoterms);
  const listsError = flow ? (vocabulary.error ?? lists.error) : null;
  // With the flow on, a deal writer edits the fields on this page wherever the stage takes an edit.
  const editsHere = flow && canEdit && editable(deal);

  // Two presses: the first arms the button, the second deletes.
  const remove = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    try {
      await deleteDeal(deal.pre_deal_id);
      notify(`Deal ${number} deleted.`);
      navigate('/deals');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <article className="deal-detail">
      <header className="register-header">
        <div>
          <h2>Deal {number}</h2>
          {/* The identifier, when the deal carries a number of its own: it is what the storage
              path and the history are keyed on. */}
          {number !== deal.pre_deal_id && (
            <p className="muted"><code>{deal.pre_deal_id}</code></p>
          )}
        </div>
        <span className="row-actions">
          {/* With the flow on, the fields are edited on this page instead. */}
          {canEdit && !flow && (
            <Link className="icon-button" to={`/deals/${deal.pre_deal_id}/edit`}
                  aria-label="Edit" title="Edit">
              <Icon name="pencil" />
            </Link>
          )}
          {canDelete && (
            <button type="button" className={`icon-button${confirming ? ' confirming' : ''}`}
                    onClick={remove} onBlur={() => setConfirming(false)}
                    aria-label={confirming ? 'Confirm delete deal' : 'Delete deal'}
                    title={confirming ? 'Click again to delete' : 'Delete deal'}>
              <Icon name="trash" />
            </button>
          )}
        </span>
      </header>

      {error && <p className="error">{error}</p>}
      {readError && (
        <p className="error">
          The deal could not be read again: {readError}{' '}
          <button type="button" className="link-button" onClick={reload}>Reload</button>
        </p>
      )}
      {listsError && <p className="error">Some of the deal's terms could not be read: {listsError}</p>}

      {flow && (
        <>
          <StagePath deal={deal} ended={ended} open={holds} />
          {holds && (
            <StageHolds stage={holds} deal={deal} events={events} labels={labels}
                        vocabulary={vocabulary} />
          )}
          {view?.open_step_for_me && (
            <DecisionPanel key={`decision-${deal.version}`} deal={deal} view={view} events={events}
                           namedPlace={namedPlace} onChanged={reload} />
          )}
          {canEdit && !isFinal(deal.stage) && view && (
            <StagePanel key={`stage-${deal.version}`} deal={deal} view={view} me={me} unsaved={unsaved}
                        namedPlace={namedPlace} onChanged={reload} />
          )}
        </>
      )}

      <dl className="deal-fields">
        {!editsHere && <Field label="Internal id" value={fields.internal_id} />}
        {flow
          ? <Field label="Stage" value={STAGE_LABELS[deal.stage]} />
          : <Field label="State" value={stateLabel(deal.state)} />}
        {flow && <FlowNotes deal={deal} events={events} />}
        {!editsHere && (
          <DealFieldList fields={fields} flow={flow} labels={labels} vocabulary={vocabulary} />
        )}
      </dl>

      {/* Mounted afresh for each version, so a save, a move or a decision shows the deal as it
          now stands. */}
      {editsHere && (
        <DealStageForm key={deal.version} deal={deal} view={view} lists={lists}
                       vocabulary={vocabulary} onChanged={reload} onDirtyChange={setUnsaved} />
      )}

      {/* No wrapping section or heading: DealDocuments brings its own, with the id its
          aria-labelledby points at. DealTimeline below does not, which is why History is wrapped
          here and Documents is not. */}
      <DealDocuments preDealId={deal.pre_deal_id} onChanged={onDocumentsChanged} />

      <section>
        <h3>History</h3>
        <DealTimeline events={events} />
      </section>

      <p><Link to="/deals">Back to the register</Link></p>
    </article>
  );
}
